import {
  findScheduleOverlap,
  MAX_SLOT_INTERVAL_MINUTES,
  MAX_SLOT_MINUTES,
  MIN_SLOT_MINUTES,
  scheduleMinuteLabel,
  SLOT_MINUTES_STEP,
  slotIntervalError,
  slotMinutesError,
  type ScheduleDay,
  type ScheduleDayBlock,
  type SchedulePatternWeekday,
} from "@care-by-ella/contracts";
import { stockholmToday } from "./onboarding-funnel";

// TES-1238 — several time windows per day in "Läkarnas scheman". Kirill works
// Tuesdays 09–12 and 13–16; the editor used to hold one window per day, so
// the second was never saved. Pure functions here, the view in
// routes/Schedule.tsx.

export const DEFAULT_WINDOW: ScheduleDayBlock = {
  startMinute: 480, // 08:00
  endMinute: 960, // 16:00
};

// The latest time a window can end at in the time pickers (22:00).
export const LAST_MINUTE = 1320;

// The window a "Lägg till tid" click adds: an hour after the day's last
// window ends, three hours long, inside the pickers' range. A day with
// nothing yet gets the ordinary 08:00–16:00.
export function nextWindow(
  blocks: readonly ScheduleDayBlock[],
): ScheduleDayBlock {
  if (blocks.length === 0) return DEFAULT_WINDOW;
  const lastEnd = Math.max(...blocks.map((b) => b.endMinute));
  const start = Math.min(lastEnd + 60, LAST_MINUTE - 30);
  return { startMinute: start, endMinute: Math.min(start + 180, LAST_MINUTE) };
}

export function windowLabel(b: ScheduleDayBlock): string {
  return `${scheduleMinuteLabel(b.startMinute)}–${scheduleMinuteLabel(b.endMinute)}`;
}

// What is wrong with one day's windows, in the words shown next to it, or
// null. The API refuses the same overlap (findScheduleOverlap is shared).
export function windowsError(
  blocks: readonly ScheduleDayBlock[],
): string | null {
  if (blocks.some((b) => b.endMinute <= b.startMinute)) {
    return "Sluttid måste vara efter starttid";
  }
  const pair = findScheduleOverlap(blocks);
  if (pair) {
    return `Tiderna ${windowLabel(pair[0])} och ${windowLabel(pair[1])} överlappar`;
  }
  return null;
}

// Windows in time order, the way they are shown and saved.
export function sortWindows(
  blocks: readonly ScheduleDayBlock[],
): ScheduleDayBlock[] {
  return [...blocks].sort((a, b) => a.startMinute - b.startMinute);
}

// ISO weekday (1 = Monday … 7 = Sunday) of a pure date.
export function isoWeekday(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow === 0 ? 7 : dow;
}

// TES-1609 — the Monday of the Stockholm week `now` falls in. The UTC date is
// still Sunday for the first hour (two in summer) of a Swedish Monday, which
// opened the schedule on the previous week.
export function stockholmMondayIso(now: Date = new Date()): string {
  const today = stockholmToday(now);
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d - (isoWeekday(today) - 1)))
    .toISOString()
    .slice(0, 10);
}

// "Use the week above as the pattern": each weekday's windows as shown. A
// day without windows becomes a free day in the pattern only if `withFreeDays`
// — by default the pattern touches only the weekdays that have hours, so
// applying it never clears a Monday nobody meant to change.
export function patternFromWeek(
  days: readonly ScheduleDay[],
  withFreeDays = false,
): SchedulePatternWeekday[] {
  return days
    .filter((d) => withFreeDays || d.blocks.length > 0)
    .map((d) => ({
      weekday: isoWeekday(d.date),
      blocks: sortWindows(d.blocks),
    }));
}

// "13 okt, 15 okt och 20 okt" — the dates a pattern would overwrite, for the
// confirm question. Long lists are cut after `max` with "och N till".
export function datesText(isoDates: readonly string[], max = 6): string {
  const fmt = new Intl.DateTimeFormat("sv-SE", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
  const label = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
    return fmt.format(new Date(Date.UTC(y, m - 1, d)));
  };
  const shown = isoDates.slice(0, max).map(label);
  const rest = isoDates.length - shown.length;
  if (rest > 0) return `${shown.join(", ")} och ${rest} till`;
  if (shown.length <= 1) return shown.join("");
  return `${shown.slice(0, -1).join(", ")} och ${shown[shown.length - 1]}`;
}

// TES-1261 — the "time between bookable start times" field, as typed. Empty
// means unset (the same as the meeting length). Returns the value to save, or
// the error shown next to the field. The API refuses the same range
// (slotIntervalError is shared).
export function parseSlotInterval(
  text: string,
  slotMinutes: number,
): { value: number | null } | { error: string } {
  const trimmed = text.trim();
  if (trimmed === "") return { value: null };
  if (!/^\d+$/.test(trimmed)) return { error: "Ange ett helt antal minuter" };
  const value = Number(trimmed);
  switch (slotIntervalError(value, slotMinutes)) {
    case "too_short":
      return {
        error: `Kan inte vara kortare än mötets längd (${slotMinutes} min)`,
      };
    case "too_long":
      return { error: `Högst ${MAX_SLOT_INTERVAL_MINUTES} minuter` };
    default:
      return { value };
  }
}

// TES-1409 — the "Möteslängd" field, as typed. Unlike the interval it cannot
// be empty: every doctor has a meeting length. Returns the value to save, or
// the error shown next to the field. The API's schema refuses the same range
// (slotMinutesError is shared).
export function parseSlotMinutes(
  text: string,
): { value: number } | { error: string } {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return { error: "Ange ett helt antal minuter" };
  const value = Number(trimmed);
  switch (slotMinutesError(value)) {
    case "out_of_range":
      return {
        error: `Mellan ${MIN_SLOT_MINUTES} och ${MAX_SLOT_MINUTES} minuter`,
      };
    case "not_step":
      return { error: `Ange minuter i steg om ${SLOT_MINUTES_STEP}` };
    default:
      return { value };
  }
}
